import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import colors from '@strava/ui/colors';
import Button from '@strava/ui/Button';
import AvatarGroup from '@strava/ui/AvatarGroup';
import Avatar from '@strava/ui/Avatar';
import I18n from 'utils/I18n';
import SvgActionsKudoHighlightedXsmall from '@strava/icons/ActionsKudoHighlightedXsmall';
import SvgActionsKudoNormalXsmall from '@strava/icons/ActionsKudoNormalXsmall';
import SvgActionsCommentNormalXsmall from '@strava/icons/ActionsCommentNormalXsmall';
import createNetworkingClient from 'utils/networking-client';
import Cldr from 'utils/Cldr';
import {
  kudosAndCommentsProps,
  viewingAthleteProps,
  entityOwnerProps,
  segAndBestEffortAchievementsProps
} from '../../../props/feed-prop-types';
import styles from './styles.scss';
import MentionableCommentForm from '../../shared/MentionableCommentForm';
import KudosCommentsAndAchievementsModal from './KudosCommentsAndAchievementsModal';
import CommentsThread from './CommentsThread';

import { useFeedEntityContext } from '../../../state/FeedEntityContext';
import {
  OPEN_MODAL_COMMENTS_TAB,
  OPEN_MODAL_KUDOS_TAB
} from '../../../state/feed-entity-actions';

const KudosAndComments = ({
  kudosAndComments,
  entity,
  entityId,
  ownedByCurrentAthlete,
  viewingAthlete,
  entityOwner,
  entityTitle,
  openCommentsModalOnIconClick,
  segAndBestEffortAchievements,
  showHairlineOnTop
}) => {
  const {
    canKudo,
    kudosCount,
    highlightedKudosers: initialLoadHighlightedKudosers,
    commentsEnabled
  } = kudosAndComments;

  const [canKudoStatus, setCanKudoStatus] = useState(canKudo);
  const [currentKudoCount, setCurrentKudoCount] = useState(kudosCount);
  const [showForm, setShowForm] = useState(false);
  const [hasKudoed, setHasKudoed] = useState(false);
  const [highlightedKudosers, setHighlightedKudosers] = useState(
    initialLoadHighlightedKudosers
  );
  const { entityState, dispatch } = useFeedEntityContext();

  const prefix = 'components.feed.social';

  const commentIconButtonTitle = () => {
    return openCommentsModalOnIconClick
      ? I18n.t(`${prefix}.view_all_comments`)
      : I18n.t(`${prefix}.icon.comments`);
  };

  const comments = entityState.comments[entityId] || [];

  const showKudosCallToAction =
    canKudoStatus && currentKudoCount === 0 && comments.length === 0;

  const kudoIconButtonTitle = () => {
    // First to give kudos
    if (showKudosCallToAction) {
      return I18n.t(`${prefix}.give_kudos`);
    }
    return canKudoStatus && currentKudoCount > 0
      ? I18n.t(`${prefix}.icon.give_kudos`)
      : I18n.t(`${prefix}.view_all_kudos`);
  };

  const handleAvatarGroupClick = () => {
    entityState.trackAnalytics({
      action: 'click',
      element: 'avatar_group',
      properties: { element_source: 'highlighted_kudosers' }
    });

    dispatch({
      type: OPEN_MODAL_KUDOS_TAB,
      payload: {
        element: 'avatar_group',
        selectedEntityId: entityId
      }
    });
  };

  const showHighlightedKudoers = () => {
    return (
      // eslint-disable-next-line
      <div
        className={styles.kudosFacePile}
        data-testid="avatar_group"
        role="img"
        onClick={handleAvatarGroupClick}
      >
        <AvatarGroup>
          {highlightedKudosers.map((kudoer) => {
            return (
              <Avatar
                name={kudoer.show_name ? kudoer.display_name : ''}
                src={kudoer.avatar_url}
                type="athlete"
                size="xsmall"
                data-testid="highlight_avatar"
                key={kudoer.display_name}
              />
            );
          })}
        </AvatarGroup>
      </div>
    );
  };

  const handleClickKudoFail = () => {
    // TODO: handle error case
  };

  const onClickKudoSuccessful = useCallback(() => {
    // Not allowed to kudo more than once
    setCanKudoStatus(false);
    setHasKudoed(true);
    setCurrentKudoCount((prev) => prev + 1);

    const highlightedKudoser = {
      avatar_url: viewingAthlete.avatarUrl,
      destination_url: `/athletes/${viewingAthlete.id}`,
      display_name: viewingAthlete.name,
      show_name: false
    };
    setHighlightedKudosers((prev) =>
      [highlightedKudoser].concat(prev.slice(0, 2))
    );
  }, [viewingAthlete.avatarUrl, viewingAthlete.id, viewingAthlete.name]);

  // group activities use the same endpoint as activities
  const entityEndpoint =
    entity === 'group_activity' ? 'activity' : entity.toLowerCase();

  const onClickKudo = (element) => {
    entityState.trackAnalytics({
      action: 'click',
      element
    });
    createNetworkingClient()
      .post(`/feed/${entityEndpoint}/${entityId}/kudo`)
      .then((response) => {
        if (response && response.status === 200) {
          onClickKudoSuccessful();
        } else {
          handleClickKudoFail();
        }
      })
      .catch(() => {
        handleClickKudoFail();
      });
  };

  useEffect(() => {
    if (entityState.kudoPosted) {
      // can only kudo an entity once...
      onClickKudoSuccessful();
    }
  }, [entityState.kudoPosted, onClickKudoSuccessful, dispatch]);

  const onClickComment = () => {
    const element = 'comment';
    if (openCommentsModalOnIconClick) {
      dispatch({
        type: OPEN_MODAL_COMMENTS_TAB,
        payload: { element, selectedEntityId: entityId }
      });
      return;
    }
    entityState.trackAnalytics({
      action: 'click',
      element
    });
    setShowForm((prev) => !prev);
  };

  // eg. "45 kudos. 12 comments"
  const kudosAndCommentsCtaText = () => {
    const components = [];
    // kudo label text when there are no kudos (Be the first to give kudos!)
    if (showKudosCallToAction) {
      components.push(
        <Button
          type="button"
          variant="text"
          className={styles.countButton}
          onClick={
            canKudoStatus
              ? () => onClickKudo('kudos_cta')
              : () =>
                  dispatch({
                    type: OPEN_MODAL_KUDOS_TAB,
                    payload: {
                      element: 'kudos_cta',
                      selectedEntityId: entityId
                    }
                  })
          }
          data-testid="give_kudos_button"
          key="give_kudos_button"
        >
          {I18n.t('components.feed.social.give_kudos')}
        </Button>
      );
    }
    // kudo label text with count (eg. 77 kudos)
    else if (currentKudoCount > 0) {
      components.push(
        <Button
          type="button"
          variant="text"
          className={styles.countButton}
          onClick={() =>
            dispatch({
              type: OPEN_MODAL_KUDOS_TAB,
              payload: { element: 'kudo_list', selectedEntityId: entityId }
            })
          }
          data-testid="kudos_count"
          key="kudos_count"
        >
          {Cldr.formatDecimal(currentKudoCount)}&nbsp;
          {I18n.t('components.feed.social.kudos')}
        </Button>
      );
      if (commentsEnabled && comments.length > 0) {
        components.push(
          <div className={styles.spacer} key="spacer" data-testid="spacer">
            &middot;
          </div>
        );
      }
    }
    // comment label text with count (eg. 48 comments )
    if (commentsEnabled && comments.length > 0) {
      components.push(
        <Button
          type="button"
          variant="text"
          className={styles.countButton}
          onClick={() =>
            dispatch({
              type: OPEN_MODAL_COMMENTS_TAB,
              payload: { element: 'comment_list', selectedEntityId: entityId }
            })
          }
          data-testid="comments_count"
          key="comments_count"
        >
          {Cldr.formatDecimal(comments.length)}&nbsp;
          {comments.length === 1
            ? I18n.t('components.feed.social.comment')
            : I18n.t('components.feed.social.comments')}
        </Button>
      );
    }
    return (
      <div className={styles.countWrapper} data-testid="counts_wrapper">
        {components}
      </div>
    );
  };

  const kudosAndCommentsIconButtons = () => {
    return (
      <>
        {/* Kudos thumb icon button */}
        <Button
          type="button"
          variant="default"
          className={styles.socialButton}
          title={kudoIconButtonTitle()}
          onClick={
            canKudoStatus
              ? () => onClickKudo('kudo')
              : () =>
                  dispatch({
                    type: OPEN_MODAL_KUDOS_TAB,
                    payload: {
                      element: 'kudo_icon',
                      selectedEntityId: entityId
                    }
                  })
          }
          data-testid="kudos_button"
        >
          {canKudoStatus || ownedByCurrentAthlete ? (
            <SvgActionsKudoNormalXsmall data-testid="unfilled_kudos" />
          ) : (
            <SvgActionsKudoHighlightedXsmall
              className={hasKudoed ? styles.kudoed : ''}
              fill={colors.O50_STRAVA_ORANGE}
              data-testid="filled_kudos"
            />
          )}

          {/* Comments icon button */}
        </Button>
        {commentsEnabled && (
          <Button
            type="button"
            variant="default"
            className={styles.socialButton}
            data-testid="comment_button"
            onClick={onClickComment}
            title={commentIconButtonTitle()}
          >
            <SvgActionsCommentNormalXsmall />
          </Button>
        )}
      </>
    );
  };

  const mentionableCommentsForm = () => (
    <form className={styles.media}>
      <div className={`${styles.mediaContent} ${styles.commentsForm}`}>
        <MentionableCommentForm
          viewingAthlete={viewingAthlete}
          entity={entity.toLowerCase()}
          entityId={entityId}
          autoFocus={true}
        />
      </div>
    </form>
  );

  // eg scenario. for activity cards, we should show hairline (divider) when there is no map,
  // no images or the visibility text is being displayed
  const containerClassNames = () => {
    const classNames = [styles.kudosAndComments];
    if (showHairlineOnTop) {
      classNames.push(styles.hairline);
    }
    return classNames.join(' ');
  };

  return (
    <>
      <div
        className={containerClassNames()}
        data-testid="kudos_comments_container"
      >
        <div className={styles.mediaBody}>
          {highlightedKudosers.length > 0 && showHighlightedKudoers()}
          {kudosAndCommentsCtaText()}
        </div>
        <div className={styles.mediaActions}>
          {kudosAndCommentsIconButtons()}
        </div>
      </div>
      {comments.length > 0 && commentsEnabled && (
        <div
          className={styles.entryComments}
          data-testid="feed-entry-comments-thread"
        >
          <CommentsThread
            onSeeMoreCommentsClick={() =>
              dispatch({
                type: OPEN_MODAL_COMMENTS_TAB,
                payload: {
                  element: 'see_more_comments',
                  selectedEntityId: entityId
                }
              })
            }
            entityId={entityId}
          />
        </div>
      )}

      {showForm && mentionableCommentsForm()}
      {entityState.openKudosCommentModal &&
        entityState.selectedEntityId === entityId && (
          <KudosCommentsAndAchievementsModal
            entity={entity}
            entityId={entityId}
            entityOwner={entityOwner}
            hasOtherAthletes={entity === 'group_activity'}
            title={entityTitle}
            viewingAthlete={viewingAthlete}
            kudosCount={currentKudoCount}
            commentsEnabled={commentsEnabled}
            segAndBestEffortAchievements={segAndBestEffortAchievements}
          />
        )}
    </>
  );
};

KudosAndComments.propTypes = {
  kudosAndComments: kudosAndCommentsProps.isRequired,
  entity: PropTypes.string.isRequired,
  entityId: PropTypes.string.isRequired,
  ownedByCurrentAthlete: PropTypes.bool.isRequired,
  viewingAthlete: viewingAthleteProps.isRequired,
  entityOwner: entityOwnerProps.isRequired,
  entityTitle: PropTypes.string.isRequired,
  segAndBestEffortAchievements: segAndBestEffortAchievementsProps,
  // (openCommentsModalOnIconClick) controls whether the kudos and comments modal is opened
  // directly by the social action buttons. This is used by group activities for example
  openCommentsModalOnIconClick: PropTypes.bool.isRequired,
  showHairlineOnTop: PropTypes.bool
};

KudosAndComments.defaultProps = {
  segAndBestEffortAchievements: [],
  showHairlineOnTop: false
};

export default KudosAndComments;
